import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { SortType } from 'rsuite-table/lib/@types/common'
import Checkbox from 'rsuite/Checkbox'
import Tooltip from 'rsuite/Tooltip'
import Table from 'rsuite/Table'
import Whisper from 'rsuite/Whisper'
import { columnModel } from './Models/columnModel'

const Column = Table.Column
const HeaderCell = Table.HeaderCell
const Cell = Table.Cell

// Responsive column sizing. rsuite computes a flexing column as
//   max((tableWidth - fixedWidths) / totalFlexGrow * flexGrow, minWidth)
// (rsuite-table/lib/utils/useCellDescriptor.js), so `minWidth` is the floor: on a wide screen
// columns share the spare width, and once the container can no longer fit every floor the
// table overflows and scrolls horizontally instead of crushing the cells.
const DEFAULT_MIN_COLUMN_WIDTH = 130
const REMARKS_MIN_COLUMN_WIDTH = 250

type Props = {
  showCheckbox?: boolean
  onChecked?: (values: Array<string>) => void
  columns: columnModel[]
  data: { [key: string]: any }[] | []
  height?: number
  showLoading?: boolean
  handleSort?: (dataKey: string, sortType?: SortType) => void
  checkedValues?: Array<string>
  rowHeight?: number
  showBorder?: boolean
  onSortColumn?: any
  params?: any
}

const RSuiteTable = (props: Props) => {
  const [loading, setLoading] = useState(false)
  const [checkedKeys, setCheckedKeys] = useState<Array<string | number>>(props?.checkedValues || [])
  let checked = false
  let indeterminate = false

  useEffect(() => {
    if (!isEmpty(props.checkedValues) && isEmpty(checkedKeys)) {
      setCheckedKeys(props?.checkedValues || [])
    }
    if (isEmpty(props.checkedValues)) {
      setCheckedKeys([])
    }
  }, [props?.checkedValues])

  if (checkedKeys.length === props?.data?.length && checkedKeys.length !== 0) {
    checked = true
  } else if (checkedKeys.length === 0) {
    checked = false
  } else if (checkedKeys.length > 0 && checkedKeys.length < props?.data?.length) {
    indeterminate = true
  }

  const handleCheckAll = (value: any, checked: boolean) => {
    const keys = checked ? props?.data.map((item: any) => item.id) : []
    setCheckedKeys(keys)
    props?.showCheckbox && props?.onChecked && props?.onChecked(keys)
  }

  const handleCheck = (value: string, checked: boolean) => {
    const keys: any = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value)
    setCheckedKeys(keys)
    props?.showCheckbox && props?.onChecked && props?.onChecked(keys)
  }

  const CheckCell = ({ rowData, onChange, checkedKeys, dataKey, ...props }: any) => (
    <Cell {...props} style={{ padding: 0 }}>
      <div style={{ lineHeight: '46px' }}>
        <Checkbox
          value={rowData[dataKey]}
          inline
          onChange={onChange}
          checked={checkedKeys.some((item: any) => item === rowData[dataKey])}
          data-cy={`${rowData?.name?.toLowerCase()}-table-body-checkbox`}
        />
      </div>
    </Cell>
  )

  const TooltipCell = ({ rowData, dataKey, isRemarks, originalCell, ...rest }: any) => {
    const dash = (
      <Cell rowData={rowData} dataKey={dataKey} {...rest}>
        <span style={isRemarks ? { fontSize: 18, fontWeight: 600, color: '#aaa' } : undefined}>
          -
        </span>
      </Cell>
    )


    const rawValue = rowData[dataKey]

    if (rawValue === undefined || rawValue === null || rawValue === '') return dash

    const isText = typeof rawValue === 'string' || typeof rawValue === 'number'

    if (!isText) return originalCell ?? dash

    if (typeof rawValue === 'string' && /<[^>]+>/.test(rawValue)) return originalCell ?? dash

    const value = rawValue
    const hasValue = value !== ''

    return (
      <Whisper
        placement='top'
        trigger={hasValue ? 'hover' : 'none'}
        speaker={<Tooltip>{value}</Tooltip>}
      >
        <Cell rowData={rowData} dataKey={dataKey} {...rest}>
          {hasValue
            ? value
            : <span style={isRemarks ? { fontSize: 18, fontWeight: 600, color: '#aaa' } : undefined}>-</span>}
        </Cell>
      </Whisper>
    )
  }

  const isRemarksColumn = (column: columnModel) =>
    (typeof column.label === 'string' && column.label.toLowerCase() === 'remarks') ||
    (typeof column.dataKey === 'string' && (column.dataKey as string).toLowerCase() === 'remarks')


  return (
    <>
      <Table
        autoHeight={true}
        data={props?.data?.filter(Boolean)}
        id='table'
        className='rs_table'
        loading={props?.showLoading || loading}
        sortColumn={props?.params?.orderBy && props?.params?.orderBy}
        sortType={props?.params?.order && props?.params?.order}
        onSortColumn={props?.onSortColumn}
        rowHeight={props.rowHeight}
        bordered={props.showBorder}

      >
        {props?.showCheckbox && (
          <Column width={50} align='center'>
            <HeaderCell style={{ padding: 0, color: '#fff' }}>
              <div style={{ lineHeight: '40px' }}>
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                  data-cy='table-header-checkbox'
                />
              </div>
            </HeaderCell>
            <CheckCell dataKey='id' checkedKeys={checkedKeys} onChange={handleCheck} />
          </Column>
        )}

        {props?.columns?.map((column: columnModel, i: number) => {
          const remarks = isRemarksColumn(column)
          // An explicitly configured `width` wins and is used as-is — only columns that never
          // declared one fall back to the responsive default. A `fixed` column is pinned while
          // the rest scroll, so rsuite needs a real width for it and it never flexes either.
          const hasExplicitWidth = typeof column?.width === 'number'
          const flexGrow =
            column?.flexGrow ?? (hasExplicitWidth || column?.fixed ? undefined : 1)
          const minWidth =
            column?.minWidth ?? (remarks ? REMARKS_MIN_COLUMN_WIDTH : DEFAULT_MIN_COLUMN_WIDTH)
          // `resizable` makes rsuite reuse the first computed width forever, which would freeze
          // a flexing column at its initial size and break the responsiveness.
          const isResizable = !flexGrow

          return remarks ? (
            <Column
              width={flexGrow ? undefined : column.width ?? REMARKS_MIN_COLUMN_WIDTH}
              flexGrow={flexGrow}
              minWidth={minWidth}
              align={column?.align ? column?.align : ('left' as any)}
              key={i}
              sortable={column?.sortable}
              fixed={column?.fixed}
              fullText={true}
              resizable={isResizable}
            >
              <HeaderCell
                style={{ color: '#fff' }}
                data-cy={`${column?.label?.toLowerCase()}-table-header`}
              >
                {column?.label}
              </HeaderCell>
              {column?.cell?.props?.dataKey
                ? <TooltipCell dataKey={column.dataKey} isRemarks={remarks} originalCell={column.cell} />
                : column.cell}
            </Column>
          ) : (
            <Column
              width={flexGrow ? undefined : column.width}
              flexGrow={flexGrow}
              minWidth={minWidth}
              align={column?.align ? column?.align : ('left' as any)}
              key={i}
              sortable={column?.sortable}
              fixed={column?.fixed}
              fullText={true}
              resizable={isResizable}
            >
              <HeaderCell
                style={{ color: '#fff' }}
                data-cy={`${column?.label?.toLowerCase()}-table-header`}
              >
                {column?.label}
              </HeaderCell>
              {column?.cell
               /*  ? <TooltipCell dataKey={column.dataKey} isRemarks={remarks} originalCell={column.cell} />
                : column.cell */}
            </Column>
          )
        })}
      </Table>
    </>
  )
}

export default RSuiteTable
